/**
 * Availability route
 * Weekly availability calendar for setting lunch/dinner presence
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import type { RootSearchParams } from '@/routes/__root';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useWeek } from '@/contexts/WeekContext';
import { useAvailabilitySummary, useSetAvailability } from '@/hooks/useAvailability';
import { useAvailabilitySync } from '@/hooks/useAvailabilitySync';
import { useUsers } from '@/hooks/useUsers';
import { useManageableUsers } from '@/hooks/useManageableUsers';
import { UserAvailabilitySection } from '@/components/availability/UserAvailabilitySection';
import { WeekNavigator } from '@/components/availability/WeekNavigator';
import { getWeekRangeForAPI } from '@/lib/utils/date';
import { requireAuth } from '@/lib/auth/routeGuards';
import { addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { authKeys } from '@/hooks/useAuth';
import { getMe } from '@/lib/api/auth';
import { UserName } from '@/components/availability/UserName';

export const Route = createFileRoute('/availability/')({
  beforeLoad: requireAuth,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as 'personal' | 'team') || 'personal',
  }),
  component: AvailabilityComponent,
});

function AvailabilityComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get root search params to preserve week when navigating
  const rootSearch = useSearch({ from: '__root__' }) as RootSearchParams;

  // Get initial user from route context - guaranteed non-null by requireAuth
  const routeContext = Route.useRouteContext();
  const { tab } = Route.useSearch();

  // Extract user with null check
  const initialUser = routeContext?.user;

  if (!initialUser) {
    return null;
  }

  // Use React Query to get latest user data (reactive to cache updates)
  const { data: authData } = useQuery({
    queryKey: authKeys.user(),
    queryFn: () => getMe(),
    initialData: { valid: true, user: initialUser },
    staleTime: Infinity, // Only refetch when explicitly invalidated
  });

  const user = authData?.user;

  if (!user) {
    return null;
  }

  // Use global week context
  const { weekStart, goToPreviousWeek, goToNextWeek, goToToday } = useWeek();

  // Enable real-time synchronization of availability data
  useAvailabilitySync();

  // Fetch manageable users for delegation support
  const { data: manageableUsers, isLoading: manageableUsersLoading } = useManageableUsers();

  // Handler to change tab via query params (preserving week param)
  const handleTabChange = (newTab: 'personal' | 'team') => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate({ search: { ...rootSearch, tab: newTab } as any });
  };

  const { startDate, endDate } = getWeekRangeForAPI(weekStart);

  // Fetch team availability summary for organizers
  const { data: summaryData, isLoading: summaryIsLoading } = useAvailabilitySummary(
    user.isOrganizer ? { startDate, endDate } : undefined
  );

  // Fetch all users for team view (organizers only)
  const { data: usersData } = useUsers({
    enabled: user.isOrganizer,
  });

  // Single availability update mutation (for team view)
  const setAvailabilityMutation = useSetAvailability();

  // Track pending updates for UI feedback
  const [pendingUpdates, setPendingUpdates] = useState<Set<string>>(new Set());

  const handleToggleAvailability = (userId: number, dateStr: string, mealType: 'lunch' | 'dinner', isCurrentlyPresent: boolean) => {
    const updateKey = `${userId}-${dateStr}-${mealType}`;

    // Add to pending updates
    setPendingUpdates(prev => new Set(prev).add(updateKey));

    // Toggle the availability state
    const newIsPresent = !isCurrentlyPresent;

    // Call the mutation
    setAvailabilityMutation.mutate(
      {
        userId,
        date: dateStr,
        mealType,
        isPresent: newIsPresent,
      },
      {
        onSuccess: () => {
          // Remove from pending on success
          setPendingUpdates(prev => {
            const next = new Set(prev);
            next.delete(updateKey);
            return next;
          });
        },
        onError: () => {
          // Remove from pending on error
          setPendingUpdates(prev => {
            const next = new Set(prev);
            next.delete(updateKey);
            return next;
          });
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('availability.title')}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {t('availability.description')}
          </p>
        </div>

        {/* Tabs for organizers */}
        {user.isOrganizer && (
          <div className="mb-6 border-b border-gray-200">
            <div className="flex space-x-8">
              <button
                onClick={() => handleTabChange('personal')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  tab === 'personal'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('availability.myAvailability')}
              </button>
              <button
                onClick={() => handleTabChange('team')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  tab === 'team'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('availability.teamAvailability')}
              </button>
            </div>
          </div>
        )}

        {/* Personal Availability - Always shown for non-organizers, tab-based for organizers */}
        {(!user.isOrganizer || tab === 'personal') && (
          <>
            {/* Week navigator */}
            <WeekNavigator
              currentWeekStart={weekStart}
              onPreviousWeek={goToPreviousWeek}
              onNextWeek={goToNextWeek}
              onToday={goToToday}
            />

            {/* Loading skeleton */}
            {manageableUsersLoading && (
              <div className="space-y-8">
                <div className="bg-gray-100 rounded-lg p-6 animate-pulse">
                  <div className="h-8 bg-gray-300 rounded w-1/4 mb-4" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
                    {Array.from({ length: 7 }).map((_, index) => (
                      <div key={index} className="bg-white rounded-lg shadow-md p-4 h-40">
                        <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto mb-4" />
                        <div className="space-y-3">
                          <div className="h-5 bg-gray-200 rounded w-3/4" />
                          <div className="h-5 bg-gray-200 rounded w-3/4" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* User availability sections - one per manageable user */}
            {!manageableUsersLoading && manageableUsers && (
              <div className="space-y-8">
                {manageableUsers.map((manageableUser) => (
                  <UserAvailabilitySection
                    key={manageableUser.id}
                    user={manageableUser}
                    weekStart={weekStart}
                    isOwnAvailability={manageableUser.id === user.id}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Team Availability Tab - Only for organizers */}
        {tab === 'team' && user.isOrganizer && (
          <>
            {/* Week navigator */}
            <WeekNavigator
              currentWeekStart={weekStart}
              onPreviousWeek={goToPreviousWeek}
              onNextWeek={goToNextWeek}
              onToday={goToToday}
            />

            {/* Loading skeleton */}
            {summaryIsLoading && (
              <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
                {Array.from({ length: 7 }).map((_, index) => (
                  <div key={index} className="space-y-2">
                    <div className="h-6 bg-gray-200 rounded w-1/4 animate-pulse" />
                    <div className="flex gap-4">
                      <div className="h-10 bg-gray-200 rounded w-1/3 animate-pulse" />
                      <div className="h-10 bg-gray-200 rounded w-1/3 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Team availability summary - by person */}
            {!summaryIsLoading && summaryData?.summary && (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 min-w-40">
                          Personne
                        </th>
                        {Array.from({ length: 7 }).map((_, dayIndex) => {
                          const date = addDays(weekStart, dayIndex);
                          return (
                            <th
                              key={`day-header-${dayIndex}`}
                              colSpan={2}
                              className="px-2 py-3 text-center text-xs font-semibold text-gray-900 border-l border-gray-300"
                            >
                              <div>{format(date, 'EEE', { locale: fr })}</div>
                              <div className="text-gray-600">{format(date, 'd/M')}</div>
                            </th>
                          );
                        })}
                      </tr>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-1"></th>
                        {Array.from({ length: 7 }).map((_, dayIndex) => (
                          <React.Fragment key={`meal-header-${dayIndex}`}>
                            <th className="px-2 py-1 text-center text-[10px] font-medium text-gray-500 border-l border-gray-200">
                              Déj
                            </th>
                            <th className="px-2 py-1 text-center text-[10px] font-medium text-gray-500 border-l border-gray-200">
                              Dîn
                            </th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Get availability map from summary data
                        const availabilityMap: Record<number, Record<string, boolean>> = {};

                        summaryData.summary.forEach((item) => {
                          const dateKey = `${item.date}-${item.mealType}`;
                          item.presentUsers.forEach((user) => {
                            if (!availabilityMap[user.userId]) {
                              availabilityMap[user.userId] = {};
                            }
                            availabilityMap[user.userId][dateKey] = true;
                          });
                        });

                        // Get all users sorted by ID
                        const allUsers = (usersData?.users || []).sort((a, b) => a.id - b.id);

                        return allUsers.map((userObj) => (
                            <tr key={userObj.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-white">
                                <UserName userId={userObj.id} />
                              </td>
                              {Array.from({ length: 7 }).map((_, dayIndex) => {
                                const date = addDays(weekStart, dayIndex);
                                const dateStr = format(date, 'yyyy-MM-dd');
                                const availability = availabilityMap[userObj.id] || {};
                                const lunchKey = `${userObj.id}-${dateStr}-lunch`;
                                const dinnerKey = `${userObj.id}-${dateStr}-dinner`;
                                const isLunchPending = pendingUpdates.has(lunchKey);
                                const isDinnerPending = pendingUpdates.has(dinnerKey);
                                const isLunchPresent = availability[`${dateStr}-lunch`] ?? false;
                                const isDinnerPresent = availability[`${dateStr}-dinner`] ?? false;

                                return (
                                  <React.Fragment key={`user-${userObj.id}-day-${dayIndex}`}>
                                    <td className="px-2 py-3 text-center text-xs border-l border-gray-200">
                                      <button
                                        onClick={() => handleToggleAvailability(userObj.id, dateStr, 'lunch', isLunchPresent)}
                                        disabled={isLunchPending}
                                        className="inline-block w-6 h-6 rounded font-semibold flex items-center justify-center transition-all cursor-pointer hover:scale-110 disabled:cursor-not-allowed"
                                        title={isLunchPresent ? 'Present for lunch' : 'Absent for lunch'}
                                      >
                                        {isLunchPending ? (
                                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                          </svg>
                                        ) : isLunchPresent ? (
                                          <span className="block w-full h-full bg-green-100 text-green-700 rounded flex items-center justify-center">✓</span>
                                        ) : (
                                          <span className="block w-full h-full bg-gray-200 text-gray-500 rounded flex items-center justify-center">—</span>
                                        )}
                                      </button>
                                    </td>
                                    <td className="px-2 py-3 text-center text-xs border-l border-gray-200">
                                      <button
                                        onClick={() => handleToggleAvailability(userObj.id, dateStr, 'dinner', isDinnerPresent)}
                                        disabled={isDinnerPending}
                                        className="inline-block w-6 h-6 rounded font-semibold flex items-center justify-center transition-all cursor-pointer hover:scale-110 disabled:cursor-not-allowed"
                                        title={isDinnerPresent ? 'Present for dinner' : 'Absent for dinner'}
                                      >
                                        {isDinnerPending ? (
                                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                          </svg>
                                        ) : isDinnerPresent ? (
                                          <span className="block w-full h-full bg-green-100 text-green-700 rounded flex items-center justify-center">✓</span>
                                        ) : (
                                          <span className="block w-full h-full bg-gray-200 text-gray-500 rounded flex items-center justify-center">—</span>
                                        )}
                                      </button>
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          ));
                      })()}
                    </tbody>
                    <tfoot>
                      <tr className="bg-purple-50 border-t-2 border-purple-200">
                        <td className="px-4 py-3 text-sm font-bold text-purple-900 sticky left-0 bg-purple-50">
                          Total
                        </td>
                        {Array.from({ length: 7 }).map((_, dayIndex) => {
                          const date = addDays(weekStart, dayIndex);
                          const dateStr = format(date, 'yyyy-MM-dd');
                          const lunchSummary = summaryData.summary.find(
                            (s) => s.date === dateStr && s.mealType === 'lunch'
                          );
                          const dinnerSummary = summaryData.summary.find(
                            (s) => s.date === dateStr && s.mealType === 'dinner'
                          );
                          const lunchCount = lunchSummary?.presentUsers.length ?? 0;
                          const dinnerCount = dinnerSummary?.presentUsers.length ?? 0;

                          return (
                            <React.Fragment key={`total-day-${dayIndex}`}>
                              <td className="px-2 py-3 text-center text-xs font-bold border-l border-purple-200 text-purple-800">
                                {lunchCount}
                              </td>
                              <td className="px-2 py-3 text-center text-xs font-bold border-l border-purple-200 text-purple-800">
                                {dinnerCount}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
